import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  ButtonLink,
  ButtonRound,
  CloseIcon,
  DocAddIcon,
  InputDragDrop,
  InputTextarea,
  ModalForm,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Cartão da imagem escolhida: miniatura, nome (abre a prévia) e remover
const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.N100};
  border-radius: 10px;
`;

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const Thumb = styled.img`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.N50};
`;

// A área da lib tem 250px de altura mínima: com ela o modal passava da tela num
// notebook (~650px úteis)
const CompactDrop = styled.div`
  > div {
    min-height: 150px;
    padding: 20px 40px;
  }
`;

const DropIcon = styled(DocAddIcon)`
  width: 40px;
  height: 40px;
`;

// Antes aceitava qualquer arquivo; o comunicado mostra a imagem
const IMAGE_ACCEPT = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
};

const TEXT_ID = "alert-text";

/**
 * Criar comunicado (sem `alert`) ou editar o texto de um (com `alert`). O PUT do
 * back não troca a imagem, então ela só entra na criação. Montado só aberto.
 *
 * Props:
 * - alert: comunicado em edição ({ id, text, image_url })
 * - onClose
 * - onSaved: depois de salvar (recarregar a lista)
 */
export default function AlertFormModal({ alert, onClose, onSaved }) {
  const alertsService = useService("alerts");
  const editing = !!alert;
  const [text, setText] = useState(alert?.text || "");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  function close() {
    if (!saving) onClose();
  }

  async function save() {
    const value = text.trim();
    if (!value) {
      // O back aceita vazio, mas um comunicado sem texto não tem o que dizer
      setError("Escreva o comunicado.");
      document.getElementById(TEXT_ID)?.focus();
      return;
    }

    setSaving(true);
    let result;
    if (editing) {
      // Em erro, o update do service devolve vazio (sem toast)
      result = await alertsService.update({ text: value }, alert.id);
      if (!result) {
        setSaving(false);
        toast.error("Erro ao atualizar o comunicado");
        return;
      }
    } else {
      // Em erro, o create do service já mostra o toast com a mensagem do back e devolve { error: true }
      // Mesmo FormData da tela antiga: "text" e a imagem em "file"
      const formData = new FormData();
      formData.append("text", value);
      if (file) formData.append("file", file);
      result = await alertsService.create(formData);
    }
    setSaving(false);
    if (!result || result.error) return;

    toast.success(editing ? "Comunicado atualizado" : "Comunicado criado. Os colaboradores já podem ver no Dashboard.");
    onSaved();
  }

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={saving}
      $isLoading={saving}
      $size="medium"
      $title={editing ? "Editar comunicado" : "Criar comunicado"}
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: saving },
        { text: editing ? "Salvar comunicado" : "Criar comunicado", action: save, variant: "primary", isDisabled: saving },
      ]}
    >
      <Body>
        <Text
          $text="O comunicado aparece para todos os colaboradores da empresa."
          $variant="paragraph_small_medium"
          $color="N700"
        />
        <InputTextarea
          $idForm={TEXT_ID}
          $label="Comunicado"
          $variant="default"
          $isRequired
          $errors={error}
          placeholder="Ex.: Férias coletivas de 22/12 a 05/01."
          rows={4}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (error) setError("");
          }}
        />
        {editing ? (
          alert.image_url ? (
            <Text $text="A imagem não muda na edição." $variant="caption_medium" $color="N400" />
          ) : null
        ) : file ? (
          <FileItem>
            <FileName>
              <Thumb src={preview} alt="" />
              <ButtonLink
                $text={file.name}
                $action={() => window.open(preview, "_blank", "noopener")}
                $noPadding
                $noWidth
              />
            </FileName>
            <ButtonRound
              title="Remover imagem"
              aria-label="Remover imagem"
              $variant="outline"
              $icon={<CloseIcon />}
              $noWidth
              $action={() => setFile(null)}
            />
          </FileItem>
        ) : (
          <CompactDrop>
            <InputDragDrop
              accept={IMAGE_ACCEPT}
              typeFile={{ valid: "image/png", message: " · São aceitas imagens PNG, JPG ou WEBP" }}
              $icon={<DropIcon />}
              $message="Arraste uma imagem (opcional) ou <strong>clique para escolher</strong>"
              onDrop={(accepted) => {
                if (accepted[0]) setFile(accepted[0]);
              }}
            />
          </CompactDrop>
        )}
      </Body>
    </ModalForm>
  );
}
